import { Import16 } from '@ingrid/iconography/react';
import { Button, Card, IconButton, ListLayout } from '@ingrid/luterkort-react';

import { Weight } from '~src/components/Weight/Weight';
import { useTranslation } from '~src/i18n/use-translation';
import { changeFileExtension, extractFileName } from '~src/utils/file';

import { useGenerateLabelsUrlMutation } from '../mutations';
import { ShipmentDetails } from '../types';
import { CustomsDeclarationPreview } from './customs-declaration-preview';

interface Props {
  shipment: Pick<ShipmentDetails, 'customs_declaration' | 'customs'>;
}

export function CustomsDeclaration({ shipment }: Props) {
  const declaration = shipment.customs_declaration?.general_customs_declaration;
  const documentUrl = shipment.customs?.document?.url;
  const mutation = useGenerateLabelsUrlMutation();

  const { t } = useTranslation();

  async function handleDownload(fileName: string) {
    /**
     * This is somewhat a temporary solution until we have a better solution in place.
     * See this conversation for further reference.
     *
     * This is far from optimal, but we currently have no other way of differentiating
     * a "mocked" link (which doesn't require signing) against a "real" one.
     *
     * https://shipwallet.slack.com/archives/C02KV8Q3X9Q/p1747996738447239
     */
    if (!fileName.endsWith('.pdf') && !fileName.endsWith('.zpl')) {
      window.open(documentUrl);
    } else {
      /**
       * Customs should be downloaded as PDF, and since the API contains a bug where it might give you the ZPL version.
       * This is a temporary workaround, where we basicaly replace `.zpl` with `.pdf`, since it should exist in storage as both.
       */
      const { data } = await mutation.mutateAsync({
        file_name: changeFileExtension(extractFileName(fileName), '.pdf'),
      });

      window.open(data.url);
    }
  }

  if (!declaration) {
    return false;
  }

  return (
    <Card.Wrapper as="section" data-testid="customs">
      <Card.Header>{t('common.customsDeclarations')}</Card.Header>
      <Card.Content>
        <ListLayout.Row>
          <ListLayout.Stack>
            <ListLayout.Row>
              <ListLayout.PrimaryText>
                {t(`shipments.customsDeclarations.incoterms.${declaration.incoterms ?? 'INCOTERMS_UNKNOWN'}`)}
              </ListLayout.PrimaryText>
            </ListLayout.Row>
            <ListLayout.SubRow gap="lg">
              <ListLayout.SecondaryText>
                {t(`shipments.customsDeclarations.contentsType.${declaration.contents_type ?? 'UNKNOWN'}`)}
              </ListLayout.SecondaryText>

              {declaration.total_gross_weight && (
                <ListLayout.SecondaryText>
                  <Weight weight={declaration.total_gross_weight} />
                </ListLayout.SecondaryText>
              )}
            </ListLayout.SubRow>
          </ListLayout.Stack>

          <ListLayout.Actions>
            <CustomsDeclarationPreview
              trigger={<Button variant="tertiary">{t('common.preview')}</Button>}
              declaration={declaration}
            />

            {documentUrl && (
              <IconButton
                onClick={() => handleDownload(documentUrl)}
                aria-label={t('common.download')}
                disabled={mutation.isPending}
                variant="tertiary"
                size="sm"
              >
                <Import16 />
              </IconButton>
            )}
          </ListLayout.Actions>
        </ListLayout.Row>
      </Card.Content>
    </Card.Wrapper>
  );
}
